"use client";

import { useEffect, useRef, useState } from "react";
import { LABEL, PILL } from "@/components/tool-kit/styles";
import {
  type DayNumber,
  localToday,
  weekdayFromMondayIndex,
} from "@/lib/tool-kit/study/dates";

/*
 * Building blocks shared by the study family's planners (assignment planner, study
 * schedule, reading planner, words-per-day calculator, class schedule).
 *
 * Same contract as `primitives.tsx`: every visible word comes in through props, from the
 * widget's own strings.
 */

/** Seven Monday-first on/off flags: [Mon, Tue, Wed, Thu, Fri, Sat, Sun]. */
export type WeekdayFlags = readonly boolean[];

export const EVERY_DAY: WeekdayFlags = [
  true,
  true,
  true,
  true,
  true,
  true,
  true,
];
export const WEEKDAYS_ONLY: WeekdayFlags = [
  true,
  true,
  true,
  true,
  true,
  false,
  false,
];

/** The flags as `weekdayOf` numbers (0 = Sunday), for the date helpers. */
export function weekdaySet(flags: WeekdayFlags): Set<number> {
  const set = new Set<number>();
  for (const [index, on] of flags.entries()) {
    if (on) {
      set.add(weekdayFromMondayIndex(index));
    }
  }
  return set;
}

/** A row of seven checkbox pills, Monday first. */
export function WeekdayToggles({
  name,
  legend,
  labels,
  value,
  onChange,
}: {
  /** Unique on the page; prefixes each checkbox's id. */
  name: string;
  legend: string;
  /** Seven short day names, Monday first. */
  labels: readonly string[];
  value: WeekdayFlags;
  onChange: (value: boolean[]) => void;
}) {
  return (
    <fieldset className="space-y-3">
      <legend className={LABEL}>{legend}</legend>
      <div className="flex flex-wrap gap-2">
        {labels.map((label, index) => (
          <label
            className={`${PILL} px-3`}
            htmlFor={`${name}-${index}`}
            key={label}
          >
            <input
              checked={value[index] ?? false}
              className="accent-primary"
              id={`${name}-${index}`}
              onChange={(event) => {
                const next = [...value];
                next[index] = event.target.checked;
                onChange(next);
              }}
              type="checkbox"
            />
            {label}
          </label>
        ))}
      </div>
    </fieldset>
  );
}

/**
 * The reader's date, read after mount.
 *
 * `null` on the server and on the first client render — both render the same "pick your
 * dates" state — then the local date. `onReady` runs once with it, which is where a
 * widget fills its date fields with sensible defaults.
 */
export function useLocalToday(
  onReady?: (today: DayNumber) => void
): DayNumber | null {
  const [today, setToday] = useState<DayNumber | null>(null);
  // A ref, so the effect runs once on mount whatever callback identity is passed.
  const ready = useRef(onReady);
  useEffect(() => {
    const value = localToday();
    setToday(value);
    ready.current?.(value);
  }, []);
  return today;
}

/*
 * localStorage that never throws.
 *
 * Private browsing, blocked site data, a full quota and some embedded webviews all make
 * `localStorage` throw rather than return null; a widget that touches it bare stops
 * working entirely in those browsers. These read as "nothing saved" and "could not
 * save" instead, so the tool keeps working and simply does not remember. Call them
 * from effects and event handlers only — never during render.
 */

export function readStored(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

/** True when the value was saved. */
export function writeStored(key: string, value: string): boolean {
  try {
    window.localStorage.setItem(key, value);
    return true;
  } catch {
    return false;
  }
}

export function removeStored(key: string): void {
  try {
    window.localStorage.removeItem(key);
  } catch {
    // Nothing was saved if storage is unavailable.
  }
}

/** Whether this browser lets the page save anything at all. */
export function storageAvailable(): boolean {
  const probe = "citationeasy-storage-probe";
  const ok = writeStored(probe, "1");
  removeStored(probe);
  return ok;
}
